import { useState, type FormEvent } from 'react'
import { Loader2, KeyRound, Trash2 } from 'lucide-react'
import { Modal, Field } from '@/shared/ui/Modal'
import { Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { setMyPin, setUserPin, clearMyPin, clearUserPin } from './api'

/**
 * Set or clear a mobile PIN.
 *
 * ## Two modes, deliberately not one
 *
 * `self` re-proves your password before it will set anything: an unlocked session someone
 * walked away from must not be convertible into permanent access to the mobile app.
 * `admin` does not ask for a password — the administrator has already proven who they are and
 * does not know the target's — and instead says plainly what it means, because setting
 * somebody's PIN is issuing a credential you can then use as them.
 */
export function SetPinDialog({
  open, onClose, onDone, mode, userId, userName, hasPin,
}: {
  open: boolean
  onClose: () => void
  onDone: () => void
  mode: 'self' | 'admin'
  /** Required in admin mode. */
  userId?: string
  userName?: string
  hasPin: boolean
}) {
  const [pin, setPin] = useState('')
  const [confirm, setConfirm] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function reset() {
    setPin(''); setConfirm(''); setPassword(''); setError(null); setBusy(false)
  }
  function close() { reset(); onClose() }

  // Checked here rather than only server-side so the user is told which of the two boxes is
  // wrong. The server re-validates and additionally rejects weak PINs (1111, 1234).
  const digitsOk = /^[0-9]{4}$/.test(pin)
  const matches = pin === confirm
  const ready = digitsOk && matches && (mode === 'admin' || password.length > 0)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      if (mode === 'self') await setMyPin(pin, password)
      else await setUserPin(userId!, pin)
      onDone()
      close()
    } catch (err) {
      setError(explain(err))
      setBusy(false)
    }
  }

  async function clear() {
    setBusy(true); setError(null)
    try {
      if (mode === 'self') await clearMyPin()
      else await clearUserPin(userId!)
      onDone()
      close()
    } catch (err) {
      setError(explain(err))
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={hasPin ? 'Change mobile PIN' : 'Set mobile PIN'}
      description={mode === 'admin'
        ? `For ${userName ?? 'this account'}`
        : 'Used to unlock the mobile app on a device you have already signed in on.'}
      footer={
        <>
          {hasPin && (
            <button type="button" className="erp-btn mr-auto" disabled={busy} onClick={clear}>
              <Trash2 className="h-3.5 w-3.5" /> Remove PIN
            </button>
          )}
          <button type="button" className="erp-btn" onClick={close}>Cancel</button>
          <button
            type="submit"
            form="set-pin-form"
            className="erp-btn erp-btn-primary"
            disabled={busy || !ready}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />}
            {hasPin ? 'Change PIN' : 'Set PIN'}
          </button>
        </>
      }
    >
      <form id="set-pin-form" onSubmit={submit} className="space-y-4">
        {error && <Note tone="bad">{error}</Note>}

        {mode === 'admin' && (
          <Note tone="bad">
            You are setting this person&apos;s PIN. Anyone who knows it can sign in to the
            mobile app as them, and your name is recorded against it. Tell them what it is
            and have them change it.
          </Note>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="PIN" required error={pin && !digitsOk ? 'Exactly four digits' : undefined}>
            <input
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={4}
              value={pin}
              // Strip non-digits as they are typed: a numeric keypad on a phone still
              // offers punctuation, and a silently rejected PIN is a confusing one.
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              className="erp-input w-full font-mono tracking-[0.5em]"
              required
            />
          </Field>
          <Field
            label="Confirm PIN"
            required
            error={confirm && !matches ? 'The two PINs do not match' : undefined}
          >
            <input
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={4}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value.replace(/\D/g, '').slice(0, 4))}
              className="erp-input w-full font-mono tracking-[0.5em]"
              required
            />
          </Field>
        </div>

        {mode === 'self' && (
          <Field label="Your password" required>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="erp-input w-full"
              required
            />
            <p className="text-erp-xs text-muted-foreground mt-1">
              Confirming your password stops an unattended session being turned into
              permanent mobile access.
            </p>
          </Field>
        )}

        <p className="text-erp-xs text-muted-foreground">
          A PIN is optional and never replaces your password. Four consecutive digits, or the
          same digit four times, are rejected.
        </p>
      </form>
    </Modal>
  )
}
